import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

/* ── Page transition variants ──────────────────────────────────── */

const pageVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: {
      duration: 0.1,
      ease: 'easeIn',
    },
  },
};

/**
 * Wraps page content with a subtle fade-and-slide animation.
 * Use inside route components to animate content on mount.
 *
 * @example
 * export function DashboardRoute() {
 *   return (
 *     <PageTransition>
 *       <div>Dashboard content...</div>
 *     </PageTransition>
 *   );
 * }
 */
export function PageTransition({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Stagger container + item ──────────────────────────────────── */

const staggerContainer = {
  enter: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.06,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 12 },
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: 'easeOut' },
  },
};

/**
 * Container that staggers its children's enter animation.
 * Each child should use the `StaggerItem` wrapper.
 *
 * @example
 * <StaggerContainer>
 *   {items.map(item => (
 *     <StaggerItem key={item.id}>
 *       <Card {...item} />
 *     </StaggerItem>
 *   ))}
 * </StaggerContainer>
 */
export function StaggerContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial="initial"
      animate="enter"
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div variants={staggerItem} className={className}>
      {children}
    </motion.div>
  );
}

/* ── Fade-in on scroll (intersection observer based) ───────────── */

/**
 * Fades in when the element enters the viewport.
 * Uses framer-motion's whileInView for zero-config intersection observer.
 */
export function FadeInView({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.35, ease: 'easeOut', delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
